import React, { useEffect, useState } from "react";
import Notification from "../../../elements/notification/Notification";
import DefaultButton from "../../../styles/materialButtonStyle";
import MaterialDialog from "../../../styles/materialDialog";
import { TextField } from "@material-ui/core";
import axios from "axios";
import { check2FA } from "../../../../utils/check2FA";
import getUserInfo from "../../../../utils/getUserInfo";
import userAuthenticationConfig from "../../../../utils/userAuthenticationConfig";
import ServiceRelationshipsSelect from "../../../elements/serviceReletionships/ServiceRelationshipsSelect";
import ProjectsSelect from "../../../elements/projects/ProjectsSelect";
import FeeTypeSelect from "../../../elements/fee/FeeTypeSelect";
import { fetchFilterData } from "../../../../utils/fetchFilterData";
import { useTranslation } from "react-i18next";

import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../../styles/styledDialog";
import { StyledRangeFeeInvoiceWrapper } from "../../styledClients";
import { parseAxiosError } from "../../../../utils/response";
import { STANDARD_ERRORS_WAY } from "../../../../utils/consts";

const RangeFeeInvoice = ({ fetchRangeFee, userId }) => {
  const { t } = useTranslation("clientsPage");
  const first = 0;
  const [open, setOpen] = useState(false);

  const defaultData = {
    "service": null,
    "project": null,
    "paymentSystem": null,
    "currency": null,
    "type": null,
    "user": null,
    "minAmount": null,
    "percent": null,
    "constant": null,
    "twofa": null
  };

  const [invoiceData, setInvoiceData] = useState(defaultData);
  const [disabled, setDisabled] = useState(false);

  const [notification, setNotification] = useState({
    visible: false,
    type: "",
    message: ""
  });

  useEffect(() => {
    setDisabled(false);
  }, [notification]);

  const findBaseFee = async (filterData) => {
    let filterUrl = fetchFilterData(filterData);
    return axios.get("/api/base-fees" + filterUrl, userAuthenticationConfig()).catch(error => {
      return { error };
    });
  };

  const createInvoice = async () => {
    setDisabled(true);

    let check2Fa = await check2FA(getUserInfo()["userId"], invoiceData.twofa);

    if (check2Fa.error) {
      setNotification({ ...notification, visible: true, type: "error", message: check2Fa.error });
      return;
    }

    const filterData = {
      "service.id": invoiceData.service.id,
      "paymentSystem.name": invoiceData.paymentSystem.name,
      "currency.asset": invoiceData.currency.asset,
      "type": invoiceData.type
    };

    let baseFee = await findBaseFee(filterData);

    if (baseFee.error) {
      parseAxiosError(baseFee.error.response.data.data.errors, notification, setNotification);
      return;
    }

    baseFee = baseFee.data["hydra:member"];

    if (baseFee.length === 0) {
      setNotification({
        ...notification,
        visible: true,
        type: "error",
        message: t("details.rangeFee.baseFeeNotFound")
      });
      return;
    }

    let data = {
      baseFee: `/api/base-fees/${baseFee[first].id}`,
      user: `api/users/${userId}`,
      minAmount: invoiceData.minAmount,
      percent: invoiceData.percent,
      constant: invoiceData.constant
    };

    axios.post("/api/range-fees", data, userAuthenticationConfig(false)).then(response => {
      if (response.status === 201) {
        fetchRangeFee();
        setNotification(
          { ...notification, visible: true, type: "success", message: t("details.rangeFee.create.success") });
      }
    }).catch(error => {
      parseAxiosError(eval(STANDARD_ERRORS_WAY), notification, setNotification);
    }).finally(() => handleClose());
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setInvoiceData(defaultData);
    setOpen(false);
  };

  return (
    <>
      <Notification
        notification={notification}
        setNotification={setNotification}
      />
      <StyledRangeFeeInvoiceWrapper className="document-head__create-invoice">
        <DefaultButton
          color="success"
          onClick={handleClickOpen}
        >
          {t("details.rangeFee.create.form.create")}
        </DefaultButton>
        <MaterialDialog
          open={open}
        >
          <StyledDialogWrapper>
            <StyledDialogHead>
              {t("details.rangeFee.create.form.title")}
            </StyledDialogHead>
            <StyledDialogContent>
              <ProjectsSelect
                checkedProjects={invoiceData.project}
                setCheckedProjects={(data) => setInvoiceData({ ...invoiceData, "project": data })}
                isObject={true}
                userId={userId}
              />
              <ServiceRelationshipsSelect
                project={invoiceData.project}
                setCheckedServiceRelationships={(data) => setInvoiceData({
                  ...invoiceData,
                  "service": data.service,
                  "currency": data.currency,
                  "paymentSystem": data.paymentSystem
                })}
              />
              <FeeTypeSelect
                checkedType={invoiceData.type}
                setCheckedType={(data) => setInvoiceData({ ...invoiceData, "type": data })}
              />
              <TextField
                id="percent"
                label={t("details.rangeFee.create.form.percent")}
                value={invoiceData.percent}
                onChange={(data) => setInvoiceData({ ...invoiceData, "percent": data.target.value })}
              />
              <TextField
                id="constant"
                label={t("details.rangeFee.create.form.constant")}
                value={invoiceData.constant}
                onChange={(data) => setInvoiceData({ ...invoiceData, "constant": data.target.value })}
              />
              <TextField
                id="minAmount"
                label={t("details.rangeFee.create.form.minAmount")}
                value={invoiceData.minAmount}
                onChange={(data) => setInvoiceData({ ...invoiceData, "minAmount": data.target.value })}
              />
              <TextField
                id="twofa"
                label={t("details.rangeFee.create.form.twoFa")}
                value={invoiceData.twofa}
                onChange={(data) => setInvoiceData({ ...invoiceData, "twofa": data.target.value })}
              />
            </StyledDialogContent>
            <StyledDialogAction col="2">
              <DefaultButton
                onClick={handleClose}
                color="danger"
                disabled={disabled}
              >
                {t("details.rangeFee.create.form.cancel")}
              </DefaultButton>
              <DefaultButton
                onClick={createInvoice}
                color="success"
                disabled={disabled}
              >
                {t("details.rangeFee.create.form.create")}
              </DefaultButton>
            </StyledDialogAction>
          </StyledDialogWrapper>
        </MaterialDialog>
      </StyledRangeFeeInvoiceWrapper>
    </>
  );
};

export default RangeFeeInvoice;